import { Component, inject, output } from '@angular/core';
import {
  AuthService,
  MultiOrderExcelImportResultComponent,
  Order,
  OrderContext,
  OrderContextService,
  ProductService,
  SimplifiedOrderContextStatus
} from 'gung-standard';
import { first, filter, switchMap, timer, takeWhile, last, tap } from 'rxjs';
import { ActionType, HlDisplayMsflowArgumentService } from '../../services/hl-display-msflow-argument.service';

@Component({
  selector: 'hl-display-multi-order-excel-import-result',
  templateUrl: './hl-display-multi-order-excel-import-result.component.html',
  styleUrl: './hl-display-multi-order-excel-import-result.component.scss',
  standalone: false
})
export class HlDisplayMultiOrderExcelImportResultComponent extends MultiOrderExcelImportResultComponent {
  protected productService = inject(ProductService);
  protected orderContextService = inject(OrderContextService);
  protected authService = inject(AuthService);
  protected hlDisplayMsFlowArgumentService = inject(HlDisplayMsflowArgumentService);

  public loaded: boolean = false;
  allowSendAll: boolean = true;
  public isPublisher: boolean = false;
  orderContexts: { [index: number]: OrderContext } = {};
  orderContextStatuses: { [index: number]: SimplifiedOrderContextStatus } = {};

  protected portalType: string = 'Customer Portal';

  public goBack = output();

  ngOnChanges(): void {
    if (!this.result) {
      return;
    }

    this.authService
      .getCurrentUser()
      .pipe(
        first(),
        filter(user => !!user)
      )
      .subscribe(user => {
        if (user.roles.includes('CUSTOMER_SERVICE')) {
          this.portalType = 'CSO Portal';
        } else if (user.roles.includes('SALES')) {
          this.portalType = 'Sales Portal';
        } else {
          this.portalType = 'Customer Portal';
        }

        super.ngOnChanges();
        this.loaded = true;
      });
  }

  createOrder(order: Order, index: number) {
    this.creatingOrders[index] = true;
    this.allowSendAll = false;

    this.applyPortalSettings(order);
    this.removeNullValues(order);

    this.orderSubmitService
      .postOrder('json/order/create/GUNG_WEB', order)
      .pipe(
        first(),
        switchMap(res =>
          timer(0, 2000).pipe(
            switchMap(() => this.orderContextService.getSimplifiedOrderContextStatus(res.contextId)),
            tap(s => (this.orderContextStatuses[index] = s)),
            takeWhile(s => !s.finalized && s.status !== 'RUNTIME_EXCEPTION', true),
            last(),
            switchMap(() => this.orderContextService.getOrderContextById(res.contextId))
          )
        )
      )
      .subscribe({
        next: context => {
          this.orderContexts[index] = context;
          this.creatingOrders[index] = false;
          if (context.status === 'RUNTIME_EXCEPTION') {
            this.errorsInCreation[index] = context.extra?.statusDetails || 'RUNTIME_EXCEPTION';
          } else {
            this.createdOrders[index] = context.erpOrderIds.join(', ');
          }
        },
        error: err => {
          this.errorsInCreation[index] = err.error?.message || err.message;
          this.creatingOrders[index] = false;
        }
      });
  }

  sendAll() {
    for (let index = 0; index < this.result?.orders.length; index++) {
      const order = this.result?.orders[index];
      this.creatingOrders[index] = true;
      this.createOrder(order, index);
    }
  }

  removeNullValues(order: Order) {
    if (order.extra && order.extra.oh) {
      Object.keys(order.extra.oh).forEach(key => {
        if (order.extra.oh[key] === null || order.extra.oh[key] === undefined) {
          delete order.extra.oh[key];
        }
      });
    }

    order.rows.forEach(row => {
      Object.keys(row).forEach(key => {
        if (row[key] === null || row[key] === undefined) {
          delete row[key];
        }
      });
      if (row.extra && row.extra.orp) {
        Object.keys(row.extra.orp).forEach(key => {
          if (row.extra.orp[key] === null || row.extra.orp[key] === undefined) {
            delete row.extra.orp[key];
          }
        });
      }
    });
  }

  applyPortalSettings(order: Order) {
    order.extra._msFlowArguments = this.hlDisplayMsFlowArgumentService.createMsFlowArguments(
      ActionType.Order,
      this.portalType
    );
  }

  navigateBack() {
    this.goBack.emit();
  }
}
